import { Badge } from "../../../ui/common-ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/common-ui/card";
import {
  AlertTriangle,
  Key,
  Globe,
  Shield,
  Zap,
  CheckCircle2,
} from "lucide-react";
import type { GraphNode } from "../types/graph";

interface BlastRadiusInspectorProps {
  node: GraphNode;
}

export function BlastRadiusInspector({ node }: BlastRadiusInspectorProps) {
  const info = node.props?.blastRadiusInfo;

  if (!info) {
    return (
      <Card className="border-border/50 bg-card shadow-lg">
        <CardContent className="p-6 text-center text-muted-foreground">
          <Zap className="w-8 h-8 mx-auto mb-2 opacity-30" />
          <p className="text-xs">No blast radius details available</p>
        </CardContent>
      </Card>
    );
  }

  const severity = info.risk?.severity || "HIGH";
  const severityStyles: Record<string, string> = {
    CRITICAL: "bg-red-500/20 text-red-500 border-red-500/30",
    HIGH: "bg-orange-500/20 text-orange-500 border-orange-500/30",
    MEDIUM: "bg-yellow-500/20 text-yellow-500 border-yellow-500/30",
    LOW: "bg-blue-500/20 text-blue-500 border-blue-500/30",
  };

  return (
    <div className="space-y-4">
      {/* Summary */}
      <Card className="border-border/50 bg-card shadow-lg">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-red-500/10">
              <Zap className="w-4 h-4 text-red-500" />
            </div>
            Blast Radius Summary
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-foreground leading-relaxed">{info.summary}</p>
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className={`text-[10px] font-semibold ${severityStyles[severity] || severityStyles.HIGH}`}>
              {severity}
            </Badge>
            {info.reachableSaas?.saasType && (
              <Badge variant="secondary" className="text-[10px]">
                {info.reachableSaas.saasType}
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Why This Matters */}
      {info.whyThisMatters && (
        <Card className="border-border/50 bg-card shadow-lg">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-orange-500/10">
                <AlertTriangle className="w-4 h-4 text-orange-500" />
              </div>
              Why This Matters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-foreground leading-relaxed">{info.whyThisMatters}</p>
          </CardContent>
        </Card>
      )}

      {/* Risk Reason */}
      {info.risk?.reason && (
        <Card className="border-border/50 bg-card shadow-lg border-l-4 border-l-red-500/50">
          <CardContent className="p-4">
            <p className="text-xs font-semibold text-red-500 mb-1">Risk</p>
            <p className="text-xs text-foreground leading-relaxed">{info.risk.reason}</p>
          </CardContent>
        </Card>
      )}

      {/* What Provides Access */}
      {info.whatProvidesAccess?.length > 0 && (
        <Card className="border-border/50 bg-card shadow-lg">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-primary/10">
                <Shield className="w-4 h-4 text-primary" />
              </div>
              Access Path
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {info.whatProvidesAccess.map((item: string, idx: number) => (
              <div key={idx} className="flex items-start gap-2 p-2 rounded-lg bg-muted/20 border border-border/30">
                <span className="text-[10px] text-primary font-bold mt-0.5">{idx + 1}.</span>
                <span className="text-xs text-foreground">{item}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Secret Pivot */}
      {info.secretPivot && (
        <Card className="border-border/50 bg-card shadow-lg">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-yellow-500/10">
                <Key className="w-4 h-4 text-yellow-500" />
              </div>
              Secret Pivot
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="p-2 rounded-lg bg-muted/20 border border-border/30">
              <p className="text-[10px] font-medium text-muted-foreground">Secret Name</p>
              <p className="text-xs font-mono text-foreground">{info.secretPivot.secretName}</p>
            </div>
            <div className="p-2 rounded-lg bg-muted/20 border border-border/30">
              <p className="text-[10px] font-medium text-muted-foreground">Type</p>
              <p className="text-xs text-foreground">{info.secretPivot.secretType}</p>
            </div>
            <p className="text-xs text-muted-foreground">{info.secretPivot.pivotReason}</p>
          </CardContent>
        </Card>
      )}

      {/* Reachable SaaS */}
      {info.reachableSaas?.repositories?.length > 0 && (
        <Card className="border-border/50 bg-card shadow-lg">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-purple-500/10">
                <Globe className="w-4 h-4 text-purple-500" />
              </div>
              Reachable Repositories
              <Badge variant="secondary" className="text-[10px]">
                {info.reachableSaas.repositories.length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {info.reachableSaas.account && (
              <div className="text-[10px] text-muted-foreground mb-2">
                Account: <span className="font-mono text-foreground">{info.reachableSaas.account}</span>
              </div>
            )}
            {info.reachableSaas.repositories.map((repo: string, idx: number) => (
              <div key={idx} className="p-2 rounded-lg bg-muted/20 border border-border/30 flex items-center gap-2">
                <Globe className="w-3 h-3 text-muted-foreground shrink-0" />
                <span className="text-xs font-mono text-foreground">{repo}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Recommended Actions */}
      {info.recommendedActions?.length > 0 && (
        <Card className="border-border/50 bg-card shadow-lg">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-green-500/10">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
              </div>
              Recommended Actions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {info.recommendedActions.map((action: string, idx: number) => (
              <div key={idx} className="flex items-start gap-2 p-2 rounded-lg bg-green-500/5 border border-green-500/20">
                <CheckCircle2 className="w-3.5 h-3.5 text-green-500 shrink-0 mt-0.5" />
                <span className="text-xs text-foreground">{action}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
